import { TabPanel, TabView } from "primereact/tabview";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { add_lease } from "../../../../utils/urls";
import Layout from "../../component/layout";
import BasicInformationTab from "./lease_tabs/BasicInformationTab";
import FinancialTermsTab from "./lease_tabs/FinancialTermsTab";
import GLAccountsTab from "./lease_tabs/GLAccountsTab";
import useLeaseFormState from "./lease_tabs/leaseFormHook";
import LeaseTermsTab from "./lease_tabs/LeaseTermsTab";
import PaymentMethodsTab from "./lease_tabs/PaymentMethodsTab";
import ReviewSubmitTab from "./lease_tabs/ReviewSubmitTab";

const AddLeaseAgreement = () => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Initialize form state using the custom hook
  const {
    formData,
    updateFormData,
    validation,
    escalations
  } = useLeaseFormState();

  const [completedTabs, setCompletedTabs] = useState(new Set());

  // Tab navigation handlers
  const handleNext = () => {
    // Mark current tab as completed
    setCompletedTabs(prev => {
      const newCompleted = new Set([...prev, activeIndex]);
      console.log("Marking tab", activeIndex, "as completed. Completed tabs:", Array.from(newCompleted));
      return newCompleted;
    });

    if (activeIndex < 5) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  // Handle direct tab click - allow access to any tab
  const handleTabChange = (e) => {
    setActiveIndex(e.index);
  };

  // Final submission handler
  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Validate all form data before submission
      const validationResult = validation.validateAll();
      if (!validationResult.isValid) {
        validationResult.errors.forEach(error => {
          toastify(error, "error");
        });
        return;
      }

      // Get facility ID for multitenancy
      const facilityId = await getItem("selectedFacilityId");
      if (!facilityId) {
        toastify("No facility selected. Please select a facility first.", "error");
        return;
      }

      // Prepare lease data for submission according to backend expectations
      const leaseData = {
        // Basic Information
        unitNumber: formData.basicInfo.unitNumber,
        tenant: formData.basicInfo.tenant,
        landlord: formData.basicInfo.landlordId,
        currency: formData.basicInfo.selectedCurrency?.value,
        status: formData.basicInfo.status,
        isNewTenant: formData.basicInfo.isNewTenant,

        // Lease Terms
        leaseTerms: {
          startDate: formData.leaseTerms.startDate,
          endDate: formData.leaseTerms.endDate,
          duration: formData.leaseTerms.leaseDuration,
          autoRenewal: formData.leaseTerms.autoRenewal
        },
        leaseTemplate: formData.leaseTerms.leaseTemplate,

        // Financial Terms
        financialTerms: {
          monthlyRent: formData.financialTerms.monthlyRent,
          paymentDueDate: formData.financialTerms.paymentDueDate,
          securityDeposit: formData.financialTerms.manualDepositEntry
            ? formData.financialTerms.securityDeposit
            : (parseFloat(formData.financialTerms.monthlyRent) || 0) * (parseInt(formData.financialTerms.depositMonths) || 1),
          balanceBroughtForward: formData.financialTerms.balanceBroughtForward,
          depositMonths: formData.financialTerms.depositMonths,
          manualDepositEntry: formData.financialTerms.manualDepositEntry,
          enabledTaxes: formData.financialTerms.enabledTaxes,
          paymentMethods: formData.financialTerms.paymentMethods,
          penaltyId: formData.financialTerms.selectedPenaltyId,
          reminderId: formData.financialTerms.selectedReminderId,
          digitaxEnabled: formData.financialTerms.digitaxEnabled,
          digitaxProfileId: formData.financialTerms.digitaxProfileId,
        },
        requireLandlordApproval: formData.financialTerms.requireLandlordApproval,

        // Billing Cycle
        billingCycle: {
          frequency: formData.billingCycle.billingFrequency,
          nextInvoiceDate: formData.billingCycle.nextInvoiceDate,
          autoSend: formData.billingCycle.autoSend
        },

        // Escalations
        escalations: escalations || [],

        // GL Accounts
        glAccounts: formData.glAccounts,

        // Additional Notes
        notes: formData.basicInfo.notes
      };

      console.log('Submitting lease data:', leaseData);

      const response = await makeRequest2(`${add_lease}/${facilityId}`, 'POST', leaseData);

      if (response.success) {
        toastify('Lease agreement created successfully', 'success');
        navigate('/facility/lease_management/leases');
      } else {
        toastify(response.message || response.error || 'Failed to create lease agreement', 'error');
      }

    } catch (error) {
      console.error('Error creating lease:', error);
      toastify('Failed to create lease agreement', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!formData) {
    return (
      <Layout>
        <div className="p-4 text-center">
          <i className="ti ti-loader-2 text-6xl text-primary animate-spin"></i>
          <p className="mt-3 text-600">Initializing form...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/facility/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/lease_management/leases">
                    Lease Management
                  </Link>
                </li>
                <li className="breadcrumb-item">Add New Lease Agreement</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5>Add New Lease Agreement</h5>
              <div className="bg-primary-50 text-primary-600 px-3 py-2 border-round font-semibold">
                Step {activeIndex + 1} of 6
              </div>
            </div>

            <div className="card-body">
              <style jsx>{`
                .lease-creation-tabs .p-tabview-nav li .p-tabview-nav-link {
                  padding: 1rem 1.5rem;
                  font-weight: 600;
                  border-radius: 0.5rem 0.5rem 0 0;
                  margin-right: 0.25rem;
                }
                .lease-creation-tabs .p-tabview-nav li.p-highlight .p-tabview-nav-link {
                  background: var(--primary-color);
                  color: white;
                }
                .lease-creation-tabs .p-tabview-panels {
                  background: transparent;
                  border: none;
                  padding: 1rem 0 0 0;
                }
                .lease-creation-tabs .p-tabview-panel {
                  background: transparent;
                  padding: 0;
                }
              `}</style>

              <TabView
                activeIndex={activeIndex}
                onTabChange={handleTabChange}
                className="lease-creation-tabs"
                style={{
                  minHeight: 'auto',
                  '--primary-color': '#6366f1',
                  '--primary-color-text': '#ffffff'
                }}
              >
                <TabPanel
                  header={
                    <div className="flex align-items-center gap-2">
                      <i className="ti ti-info-circle"></i>
                      <span>Basic Information</span>
                      {completedTabs.has(0) && <i className="ti ti-check" style={{ color: '#10b981', marginLeft: '0.5rem' }}></i>}
                    </div>
                  }
                >
                  <BasicInformationTab
                    formData={formData}
                    updateFormData={updateFormData}
                    onNext={handleNext}
                    validation={validation}
                  />
                </TabPanel>

                <TabPanel
                  header={
                    <div className="flex align-items-center gap-2">
                      <i className="ti ti-calendar"></i>
                      <span>Lease Terms</span>
                      {completedTabs.has(1) && <i className="ti ti-check" style={{ color: '#10b981', marginLeft: '0.5rem' }}></i>}
                    </div>
                  }
                >
                  <LeaseTermsTab
                    formData={formData}
                    updateFormData={updateFormData}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    validation={validation}
                  />
                </TabPanel>

                <TabPanel
                  header={
                    <div className="flex align-items-center gap-2">
                      <i className="ti ti-currency-dollar"></i>
                      <span>Financial Terms</span>
                      {completedTabs.has(2) && <i className="ti ti-check" style={{ color: '#10b981', marginLeft: '0.5rem' }}></i>}
                    </div>
                  }
                >
                  <FinancialTermsTab
                    formData={formData}
                    updateFormData={updateFormData}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    validation={validation}
                  />
                </TabPanel>

                <TabPanel
                  header={
                    <div className="flex align-items-center gap-2">
                      <i className="ti ti-credit-card"></i>
                      <span>Payment Methods</span>
                      {completedTabs.has(3) && <i className="ti ti-check" style={{ color: '#10b981', marginLeft: '0.5rem' }}></i>}
                    </div>
                  }
                >
                  <PaymentMethodsTab
                    formData={formData}
                    updateFormData={updateFormData}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    validation={validation}
                  />
                </TabPanel>

                <TabPanel
                  header={
                    <div className="flex align-items-center gap-2">
                      <i className="ti ti-calculator"></i>
                      <span>GL Accounts</span>
                      {completedTabs.has(4) && <i className="ti ti-check" style={{ color: '#10b981', marginLeft: '0.5rem' }}></i>}
                    </div>
                  }
                >
                  <GLAccountsTab
                    formData={formData}
                    updateFormData={updateFormData}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    validation={validation}
                  />
                </TabPanel>

                <TabPanel
                  header={
                    <div className="flex align-items-center gap-2">
                      <i className="ti ti-check-circle"></i>
                      <span>Review & Submit</span>
                      {completedTabs.has(5) && <i className="ti ti-check" style={{ color: '#10b981', marginLeft: '0.5rem' }}></i>}
                    </div>
                  }
                >
                  <ReviewSubmitTab
                    formData={formData}
                    updateFormData={updateFormData}
                    escalations={escalations}
                    onPrevious={handlePrevious}
                    onSubmit={handleSubmit}
                    submitting={submitting}
                  />
                </TabPanel>
              </TabView>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AddLeaseAgreement;